import React, { useRef, useState, useEffect } from 'react';
import { styles } from '../../../../utils/styles';

type FieldConfigShowTitleSectionProps = {
    id?: string;
    showTitle?: boolean;
    onShowTitleChange?: (value: boolean) => void;
    displayName?: string;
    onDisplayNameChange?: (value: string) => void;
}

export const FieldConfigShowTitleSection: React.FC<FieldConfigShowTitleSectionProps> = ({
    id,
    showTitle = true,
    onShowTitleChange,
    displayName = '',
    onDisplayNameChange
}) => {
    const baseId = id || 'field-config-show-title-section';

    // Debouncing for display name input
    const displayNameDebounceRef = useRef<NodeJS.Timeout>();
    const [localDisplayName, setLocalDisplayName] = useState<string>(displayName);

    // Sync local state when prop changes externally
    useEffect(() => {
        setLocalDisplayName(displayName);
    }, [displayName]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (displayNameDebounceRef.current) {
                clearTimeout(displayNameDebounceRef.current);
            }
        };
    }, []);

    // Debounced handler for display name changes
    const handleDebouncedDisplayNameChange = (value: string) => {
        setLocalDisplayName(value);
        if (displayNameDebounceRef.current) {
            clearTimeout(displayNameDebounceRef.current);
        }
        displayNameDebounceRef.current = setTimeout(() => {
            onDisplayNameChange && onDisplayNameChange(value);
        }, 500);
    };

    return (
        <div id={baseId} data-testid={baseId} className="tw-mb-4">
            <label id={`${baseId}-checkbox-label`} className="tw-flex tw-items-center">
                <input
                    id={`${baseId}-checkbox`}
                    data-testid={`${baseId}-checkbox`}
                    type="checkbox"
                    checked={showTitle}
                    onChange={(e) => onShowTitleChange && onShowTitleChange(e.target.checked)}
                    className={styles.checkboxInput}
                />
                <span id={`${baseId}-checkbox-text`} className={styles.checkboxLabel}>Show Title</span>
            </label>

            {/* Display Name Input - shown when Show Title is checked */}
            {showTitle && (
                <div id={`${baseId}-display-name-wrapper`} className="tw-mt-3">
                    <label id={`${baseId}-display-name-label`} className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2 tw-ml-6">
                        Display Name
                    </label>
                    <div id={`${baseId}-display-name-input-wrapper`} className="tw-ml-6">
                        <input
                            id={`${baseId}-display-name-input`}
                            data-testid={`${baseId}-display-name-input`}
                            type="text"
                            value={localDisplayName}
                            onChange={(e) => handleDebouncedDisplayNameChange(e.target.value)}
                            className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2"
                            placeholder="Enter display name"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
